/**
 * External dependencies
 */
import type { Attributes } from '../../../../../packages/conversion-action-library/time-on-page/types';

/**
 * Internal dependencies
 */
import type { ConvertibleAction, Convert } from '../../types';

const MILLISECONDS_IN_SECOND = 1000;

export function listenToTimeOnPageEvent(
	allActions: ReadonlyArray< ConvertibleAction >,
	convert: Convert
): void {
	const actions = allActions.filter( isTimeOnPageAction );
	if ( ! actions.length ) {
		return;
	}

	const actionsByTime = actions.reduce(
		( result, action ) => {
			const key = `${ action.attributes.value }`;
			result[ key ] = [ ...( result[ key ] ?? [] ), action ];
			return result;
		},
		{} as Record< string, ReadonlyArray< ConvertibleAction< Attributes > > >
	);

	Object.keys( actionsByTime ).forEach( ( time ) => {
		if ( ! actionsByTime[ time ] ) {
			return;
		}
		const trackedActions = actionsByTime[ time ];

		let timer: NodeJS.Timeout | undefined;
		let remainingTime = Number.parseInt( time ) * MILLISECONDS_IN_SECOND;
		let startTime = Date.now();

		function startTimer() {
			if ( remainingTime < 0 ) {
				return;
			}

			startTime = Date.now();
			timer = setTimeout( () => {
				convert( trackedActions );
			}, remainingTime );
		}

		function pauseTimer() {
			if ( ! timer ) {
				return;
			}

			clearTimeout( timer );
			timer = undefined;
			remainingTime -= Date.now() - startTime;
		}

		if ( ! document.hidden ) {
			startTimer();
		}

		document.addEventListener( 'visibilitychange', () => {
			if ( document.hidden ) {
				pauseTimer();
			} else {
				startTimer();
			}
		} );

		window.addEventListener( 'blur', pauseTimer );
		window.addEventListener( 'focus', startTimer );
	} );
}

// =======
// HELPERS
// =======

function isTimeOnPageAction(
	action: ConvertibleAction
): action is ConvertibleAction< Attributes > {
	return 'nab/time-on-page' === action.type;
}
